import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useStripeSubscription } from "@/hooks/useStripeSubscription";
import { useUserAccessCheck } from "@/hooks/useUserAccessCheck";
import { useUserRole } from "@/hooks/useUserRole";
import { UserAccessBlockedScreen } from "@/components/UserAccessBlockedScreen";
import { Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ExternalLink, Store } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireActiveSubscription?: boolean;
}

// Routes that should always be accessible (no subscription required)
const ALWAYS_ALLOWED_ROUTES = [
  "/dashboard/loja",
  "/dashboard/configuracoes",
  "/dashboard/settings",
  "/dashboard/perfil",
  "/dashboard/notificacoes",
];

export const ProtectedRoute = ({ children, requireActiveSubscription = true }: ProtectedRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();
  const { subscription, loading: subLoading, openCustomerPortal } = useStripeSubscription();
  const { hasAccess, reason, message, isLoading: accessLoading } = useUserAccessCheck();
  const { role, isLoading: roleLoading } = useUserRole();

  // Check if user is a system admin (has owner or admin role)
  const isPrivilegedUser = role === 'admin' || role === 'owner';
  
  // Check if current route should always be allowed
  const isAlwaysAllowedRoute = ALWAYS_ALLOWED_ROUTES.some(route => 
    location.pathname.startsWith(route)
  );

  // Skip subscription loading for privileged users or always-allowed routes
  const needsSubscriptionCheck = requireActiveSubscription && !isPrivilegedUser && !isAlwaysAllowedRoute;
  
  // Wait for all loading states to complete
  const isLoading = authLoading || accessLoading || roleLoading || (needsSubscriptionCheck && subLoading);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check if user is blocked due to limit exceeded
  if (!hasAccess && reason) {
    return <UserAccessBlockedScreen reason={reason} message={message} />;
  }

  // Privileged users (owner/admin) or always-allowed routes bypass subscription check
  if (isPrivilegedUser || isAlwaysAllowedRoute) {
    return <>{children}</>;
  }

  // Check subscription status if required
  if (requireActiveSubscription) {
    const isActive = subscription.status === "active" || subscription.status === "trialing";
    const isExpired = subscription.status === "canceled" || subscription.status === "cancelled";
    const isPastDue = subscription.status === "past_due";
    
    // If subscribed is true, allow access regardless of status parsing
    const hasValidSubscription = subscription.subscribed || isActive;
    
    // If canceled but still has access until period end, allow access
    const canceledButValid = isExpired && subscription.subscriptionEnd && new Date(subscription.subscriptionEnd) > new Date();
    
    // Show block screen only if truly expired
    if (!hasValidSubscription && !canceledButValid) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <Card className="max-w-md w-full">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-orange-500/10 flex items-center justify-center">
                <AlertTriangle className="w-8 h-8 text-orange-500" />
              </div>
              <CardTitle className="text-xl">
                {isExpired ? "Assinatura Expirada" : isPastDue ? "Pagamento Pendente" : "Ative sua Conta"}
              </CardTitle>
              <CardDescription>
                {isExpired 
                  ? "Sua assinatura expirou. Adquira módulos para continuar usando a plataforma."
                  : isPastDue 
                  ? "Existe um pagamento pendente na sua conta. Regularize para continuar usando."
                  : "Adquira módulos na nossa loja para começar a usar a plataforma."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button 
                className="w-full" 
                variant="brand" 
                onClick={() => window.location.href = "/dashboard/loja"}
              >
                <Store className="w-4 h-4 mr-2" />
                Ir para Loja de Módulos
              </Button>
              {subscription.subscribed && (
                <Button className="w-full" variant="outline" onClick={openCustomerPortal}>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Gerenciar Assinatura
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }
  }

  return <>{children}</>;
};